import {
  Chart,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Title,
  Tooltip,
} from 'chart.js';
import { LeadReportSummary } from './leadReportUtils';

Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Legend, Title, Tooltip);

const STATUS_COLORS = [
  '#38BDF8', // novo
  '#3B82F6', // em_atendimento
  '#F59E0B', // negociacao
  '#8B5CF6', // proposta
  '#16A34A', // ganho
  '#DC2626', // perdido
  '#6B7280', // pos_venda
];

const ORIGIN_COLORS = [
  '#2563EB',
  '#0EA5E9',
  '#14B8A6',
  '#22C55E',
  '#EAB308',
  '#F97316',
  '#EF4444',
  '#A855F7',
  '#64748B',
  '#94A3B8',
];

const renderChartToDataUrl = (
  type: 'doughnut' | 'bar',
  labels: string[],
  values: number[],
  colors: string[],
  title: string,
  width: number,
  height: number
): string | null => {
  if (!labels.length || values.every((v) => v <= 0)) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const chart = new Chart(ctx, {
    type,
    data: {
      labels,
      datasets: [
        {
          label: title,
          data: values,
          backgroundColor: colors.slice(0, labels.length),
          borderColor: '#ffffff',
          borderWidth: type === 'doughnut' ? 2 : 0,
          borderRadius: type === 'bar' ? 4 : 0,
        },
      ],
    },
    options: {
      animation: false,
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: title,
          font: { size: 16, weight: 'bold' },
          color: '#111827',
          padding: { bottom: 12 },
        },
        legend: {
          display: type === 'doughnut',
          position: 'bottom',
          labels: { boxWidth: 12, font: { size: 11 }, color: '#374151' },
        },
        tooltip: { enabled: false },
      },
      scales:
        type === 'bar'
          ? {
              x: {
                ticks: { color: '#4B5563', font: { size: 10 }, maxRotation: 45, minRotation: 0 },
                grid: { display: false },
              },
              y: {
                beginAtZero: true,
                ticks: { precision: 0, color: '#4B5563', font: { size: 10 } },
                grid: { color: '#E5E7EB' },
              },
            }
          : undefined,
    },
  });

  const dataUrl = canvas.toDataURL('image/png');
  chart.destroy();
  return dataUrl;
};

export type LeadReportChartImages = {
  statusChart: string | null;
  originChart: string | null;
  sellerChart: string | null;
};

export const buildLeadReportChartImages = (
  summary: LeadReportSummary
): LeadReportChartImages => {
  const statusItems = summary.byStatus.filter((s) => s.count > 0);
  const originItems = summary.byOrigin.filter((o) => o.count > 0).slice(0, 8);
  const sellerItems = summary.bySeller.filter((s) => s.count > 0).slice(0, 8);

  return {
    statusChart: renderChartToDataUrl(
      'doughnut',
      statusItems.map((s) => s.label),
      statusItems.map((s) => s.count),
      STATUS_COLORS,
      'Leads por status',
      640,
      420
    ),
    originChart: renderChartToDataUrl(
      'bar',
      originItems.map((o) => o.label),
      originItems.map((o) => o.count),
      ORIGIN_COLORS,
      'Leads por origem',
      640,
      420
    ),
    sellerChart: renderChartToDataUrl(
      'bar',
      sellerItems.map((s) => s.label),
      sellerItems.map((s) => s.count),
      ORIGIN_COLORS,
      'Leads por vendedor',
      640,
      420
    ),
  };
};
